import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type LineStats = {
  line_slug: string;
  total_skus: number;
  productos_base: number;
  con_color: number;
  con_packaging: number;
  colores_diferentes: number;
  msrp_promedio: number;
  cost_promedio: number;
  ya_importados: number;
};

type StagingRow = {
  sku: string;
  sku_base: string;
  name: string | null;
  finish: string | null;
  msrp_usd: number;
  cost_usd: number;
  specs: any;
};

const LINE_META: Record<string, { name: string; color: string; description: string }> = {
  caseta:    { name: 'Caséta',     color: '#5B9BD5', description: 'Entry residencial smart' },
  radiora3:  { name: 'RadioRA 3',  color: '#B08D5B', description: 'Sistema mid residencial sofisticado' },
  maestro:   { name: 'Maestro',    color: '#4A4A4A', description: 'Cableado profesional, slide-to-dim icónico' },
  wallbox:   { name: 'Wallbox',    color: '#E07B3F', description: 'Diva, Skylark, Claro, Ariadni — value tier' },
  vive:      { name: 'Vive',       color: '#3D8B7A', description: 'Sistema comercial wireless' },
  homeworks: { name: 'HomeWorks',  color: '#C9A961', description: 'Premium residencial · Palladiom, Alisse' },
  shading:   { name: 'Shading',    color: '#888888', description: 'Sivoia, Triathlon, Palladiom shading' },
  athena:    { name: 'Athena',     color: '#666666', description: 'Sistema legacy' },
  lumaris:   { name: 'Lumaris',    color: '#777777', description: 'Línea reciente' },
};

export function AdminImportarLutron() {
  const [lines, setLines] = useState<LineStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLine, setSelectedLine] = useState<string | null>(null);
  const [preview, setPreview] = useState<StagingRow[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => { reload(); }, []);

  async function reload() {
    setLoading(true);
    const { data, error } = await supabase.rpc('lutron_staging_stats');
    if (error) {
      console.error('lutron_staging_stats error:', error);
      setLoading(false);
      return;
    }
    setLines(
      ((data as any[]) ?? []).map(r => ({
        line_slug: r.line_slug,
        total_skus: Number(r.total_skus),
        productos_base: Number(r.productos_base),
        con_color: Number(r.con_color),
        con_packaging: Number(r.con_packaging),
        colores_diferentes: Number(r.colores_diferentes),
        msrp_promedio: Number(r.msrp_promedio),
        cost_promedio: Number(r.cost_promedio),
        ya_importados: Number(r.ya_importados),
      }))
    );
    setLoading(false);
  }

  async function loadPreview(lineSlug: string) {
    setSelectedLine(lineSlug);
    setPreviewLoading(true);
    setPreview([]);
    const { data } = await supabase
      .from('lutron_import_staging')
      .select('sku, sku_base, name, finish, msrp_usd, cost_usd, specs')
      .eq('line_slug', lineSlug)
      .order('sku_base')
      .limit(100);
    setPreview((data as StagingRow[]) ?? []);
    setPreviewLoading(false);
  }

  async function promote(lineSlug: string, dryRun: boolean = false) {
    setPromoting(true);
    setMsg(null);
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/promote-lutron-line`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ line_slug: lineSlug, dry_run: dryRun }),
    });
    const data = await res.json();
    setPromoting(false);
    if (data.ok) {
      setMsg({
        type: 'success',
        text: dryRun
          ? `Dry-run OK: importaría ${data.products_to_insert} productos + ${data.variants_to_insert} variantes`
          : `✓ Importados ${data.products_inserted} productos + ${data.variants_inserted} variantes`,
      });
      if (!dryRun) reload();
    } else {
      setMsg({ type: 'error', text: data.error ?? 'Error desconocido' });
    }
  }

  return (
    <AdminContainer
      title="Importar catálogo Lutron"
      eyebrow="Sandbox staging · 4,194 SKUs disponibles"
    >
      <div style={{
        padding: 14, marginBottom: 20,
        background: theme.bgFooter, border: `1px solid ${theme.border}`,
        borderLeft: `4px solid ${theme.accent}`,
        fontSize: 13, color: theme.brand, lineHeight: 1.6,
      }}>
        <strong>Flujo:</strong> Los 4,194 SKUs Lutron del Excel oficial están en sandbox. Promueve <strong>una línea a la vez</strong> a tu catálogo público. Cada SKU base genera 1 producto; los sufijos de color generan variantes. Specs (voltaje, watts, neutral, etc.) se extraen de la descripción Lutron.<br />
        <span style={{ color: theme.textMuted, fontSize: 12 }}>Idempotente: re-ejecutar reemplaza si ya estaba importado.</span>
      </div>

      {loading && <div style={{ color: theme.textMuted }}>Cargando estadísticas…</div>}

      {!loading && (
        <div style={{
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        }}>
          {lines.map(line => {
            const meta = LINE_META[line.line_slug] ?? {
              name: line.line_slug, color: theme.textMuted, description: '',
            };
            const isImported = line.ya_importados === line.total_skus;
            return (
              <div key={line.line_slug} style={{
                background: theme.bg,
                border: `1.5px solid ${selectedLine === line.line_slug ? meta.color : theme.border}`,
                borderTop: `4px solid ${meta.color}`,
                padding: 16,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, fontWeight: 600 }}>
                      {meta.name}
                    </div>
                    <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                      {meta.description}
                    </div>
                  </div>
                  {isImported && (
                    <span style={{
                      fontSize: 9, padding: '2px 6px',
                      background: theme.accentDark, color: '#fff',
                      textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700,
                    }}>✓ Importada</span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14, fontSize: 12 }}>
                  <Stat label="SKUs" value={line.total_skus} />
                  <Stat label="Productos" value={line.productos_base} />
                  <Stat label="Con color" value={line.con_color} />
                  <Stat label="Colores" value={line.colores_diferentes} />
                  <Stat label="MSRP avg" value={`$${line.msrp_promedio.toFixed(0)}`} />
                  <Stat label="Cost avg" value={`$${line.cost_promedio.toFixed(0)}`} subtle />
                </div>

                <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                  <button onClick={() => loadPreview(line.line_slug)} style={btnSmall}>
                    👁 Preview
                  </button>
                  <button
                    onClick={() => promote(line.line_slug, false)}
                    disabled={promoting}
                    style={{ ...btnPrimary, flex: 1, opacity: promoting ? 0.5 : 1 }}
                  >
                    {promoting && selectedLine === line.line_slug ? 'Importando…' : `Importar ${meta.name} →`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {msg && (
        <div style={{
          marginTop: 20, padding: 14,
          background: msg.type === 'success' ? 'rgba(87,255,154,0.08)' : 'rgba(220,38,38,0.05)',
          border: `1px solid ${msg.type === 'success' ? theme.accentDark : theme.err}`,
          color: msg.type === 'success' ? theme.accentDark : theme.err,
          fontSize: 13,
        }}>
          {msg.text}
        </div>
      )}

      {selectedLine && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{
            fontFamily: theme.font.head, fontSize: 20, color: theme.brand,
            margin: '0 0 12px', fontWeight: 500,
          }}>
            Preview · {LINE_META[selectedLine]?.name ?? selectedLine}
          </h3>
          {previewLoading && <div style={{ color: theme.textMuted }}>Cargando preview…</div>}
          {!previewLoading && preview.length > 0 && (
            <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
                <thead>
                  <tr style={{ background: theme.bgSoft }}>
                    <Th>SKU</Th>
                    <Th>Producto</Th>
                    <Th>Color</Th>
                    <Th>Specs</Th>
                    <Th style={{ textAlign: 'right' }}>MSRP USD</Th>
                    <Th style={{ textAlign: 'right' }}>Cost USD</Th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.sku} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <Td><code style={{ fontFamily: theme.font.mono, fontSize: 10 }}>{p.sku}</code></Td>
                      <Td>{(p.name ?? '').slice(0, 50)}</Td>
                      <Td style={{ fontSize: 11 }}>{p.finish ?? '—'}</Td>
                      <Td style={{ fontSize: 10, color: theme.textMuted }}>
                        {p.specs?.voltage && <span>{p.specs.voltage}</span>}
                        {p.specs?.led_watts && <span> · {p.specs.led_watts}W LED</span>}
                        {p.specs?.button_count && <span> · {p.specs.button_count}btn</span>}
                        {p.specs?.control_type && <span> · {p.specs.control_type}</span>}
                      </Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600, color: theme.brand }}>
                        ${Number(p.msrp_usd).toFixed(2)}
                      </Td>
                      <Td style={{ textAlign: 'right', color: theme.textMuted }}>
                        ${Number(p.cost_usd).toFixed(2)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length === 100 && (
                <div style={{ padding: 10, fontSize: 11, color: theme.textMuted, textAlign: 'center', borderTop: `1px solid ${theme.border}` }}>
                  Mostrando 100 de la línea. Hay más SKUs en la importación.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </AdminContainer>
  );
}

function Stat({ label, value, subtle }: { label: string; value: any; subtle?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{
        fontFamily: theme.font.head, fontSize: subtle ? 14 : 18,
        fontWeight: 600, color: subtle ? theme.textMuted : theme.brand,
        marginTop: 2,
      }}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left', padding: '10px 12px',
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, ...style,
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 12px', verticalAlign: 'top', ...style }}>{children}</td>;
}

const btnPrimary: React.CSSProperties = {
  background: theme.accent, color: theme.brand, border: 'none',
  padding: '8px 14px', fontSize: 11, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const btnSmall: React.CSSProperties = {
  background: 'transparent', color: theme.brand,
  border: `1px solid ${theme.border}`,
  padding: '8px 12px', fontSize: 11, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};
